import React, { useContext, useCallback, useState, useEffect } from 'react'
import { AuthContext } from '../../../context/firebase-context'
import { useNavigate, Link } from 'react-router-dom'
import message from 'antd/lib/message'
import { getUserData, sendVerificationEmail } from '../../../services/firebase'
import { firebaseErrorMessage } from '../../../common/firebase-error'
import { ReactComponent as FinalBitLogo } from '../../../assets/icons/logo.svg'
import { ReactComponent as ShieldCheck } from './shield-check.svg'
import styles from './VerifyEmail.module.scss'

/** Said only when the rejection itself gives us nothing to say. */
const RESEND_FAILED = 'Can’t send verification link. Please contact support.'

/**
 * The gate a signed-up but unverified account lands on.
 *
 * Rendered two ways by `RequireAuth`: as the whole screen for accounts created
 * after the verification cutoff, and stacked above the app for older ones. It
 * is therefore laid out as a full-viewport *block*, not a fixed overlay — going
 * fixed would blank the app out from under the second case.
 *
 * Dark on purpose in both theme modes: this is a takeover screen with its own
 * surface, so it does not read `themeMode`.
 */
const VerifyEmail = () => {
  const user = useContext(AuthContext)
  const navigate = useNavigate()
  const [emailResend, setEmailResend] = useState(false)
  const [messageApi, contextHolder] = message.useMessage()

  const onClick = useCallback(async () => {
    try {
      await sendVerificationEmail()
      messageApi.success(`We sent your verification link to ${user.email}`, 10)
      setEmailResend(true)
    } catch (error) {
      // Firebase's own reason first — a resend refused for being the fourth in
      // a minute is a wait, not a support ticket, and the fixed line sent
      // people to a human for it.
      messageApi.error(firebaseErrorMessage(error, RESEND_FAILED))
    }
  }, [])

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const user = await getUserData()
        user.reload()
        if (user.emailVerified) {
          navigate(0)
          clearInterval(interval)
        }
      } catch (e) {
        console.log(e)
      }
    }, 5000)
    return () => {
      clearInterval(interval)
    }
  }, [])

  return (
    <div className={styles.screen}>
      {contextHolder}
      <span className={styles.glowTop} aria-hidden='true' />
      <span className={styles.glowBottom} aria-hidden='true' />

      <FinalBitLogo className={styles.logo} aria-hidden='true' focusable='false' />

      <div className={styles.content}>
        <span className={styles.badge}>
          <ShieldCheck className={styles.badgeIcon} aria-hidden='true' focusable='false' />
        </span>

        <h1 className={styles.title}>
          Verify
          <span className={styles.titleLine}>
            <span className={styles.titleThin}>your </span>email
          </span>
        </h1>

        <div className={styles.details}>
          <p className={styles.lead}>We sent a verification link to</p>
          <p className={styles.email}>{user.email}</p>
          <p className={styles.note}>
            Check your inbox or <strong>spam</strong> folder to complete your registration.
          </p>
        </div>
      </div>

      <div className={styles.footer}>
        <div className={styles.footerInner}>
          <p className={styles.footerNote}>Didn’t receive it? Request a new link below.</p>
          <div className={styles.actions}>
            <button
              type='button'
              className={styles.resendButton}
              disabled={emailResend}
              onClick={onClick}
            >
              Request new link
            </button>
            <Link to='/logout' className={styles.logoutButton}>Log out</Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VerifyEmail
